/*

class to generate condition

http://www.hl7.org/fhir/condition.html#Condition

{
  "conditionId" : "<string>"
  "eocId" : "<string>"
  "state" : "<code>" // active || pending || archive || delete
  "clinicalStatus" : { code }, // C? active | recurrence | relapse | inactive | remission | resolved
  "verificationStatus" : { code }, // C? unconfirmed | provisional | differential | confirmed | refuted | entered-in-error
  "category" : [{ code }], // problem-list-item | encounter-diagnosis
  "severity" : { code }, // Subjective severity of condition
  "code" : { code }, // Identification of the condition, problem or diagnosis
  "bodySite" : [{ code }], // Anatomical location, if relevant
  "subject" : { Reference(Patient|Group) }, // R!  Who has the condition?

  // onset[x]: Estimated or actual date,  date-time, or age. One of these 5:
  "onsetDateTime" : "<dateTime>",
  "onsetAge" : { Age },
  "onsetPeriod" : { Period },
  "onsetRange" : { Range },
  "onsetString" : "<string>",

  // abatement[x]: When in resolution/remission. One of these 5:
  "abatementDateTime" : "<dateTime>",
  "abatementAge" : { Age },
  "abatementPeriod" : { Period },
  "abatementRange" : { Range },
  "abatementString" : "<string>",
  "recordedDate" : "<dateTime>", // Date record was first recorded
  "recorder" : { Reference(Practitioner|PractitionerRole|Patient| RelatedPerson) }, // Who recorded the condition
  "asserter" : { Reference(Practitioner|PractitionerRole|Patient| RelatedPerson) }, // Person who asserts this condition
  "stage" : [{ // Stage/grade, usually assessed formally
    "summary" : { code }, // C? Simple summary (disease specific)
    "assessment" : [{ Reference(ClinicalImpression|DiagnosticReport|Observation) }], // C? Formal record of assessment
    "type" : { code } // Kind of staging
  }],
  "evidence" : [{ // Supporting evidence
    "code" : [{ code }], // C? Manifestation/symptom
    "detail" : [{ Reference(Any) }] // C? Supporting information found elsewhere
  }],
}

*/

// Import libraries
const Joi = require('joi');

// Import variables
const { STATES } = require('@bcm/constants');
const { DataTableBase } = require('./base.class');
const { joiValidateObj, identifiers } = require('../datastructComponents');
const { codeSchema } = require('./code.class');
const { evidenceSchema } = require('./evidence.class');
const { stageSchema } = require('./stage.class');

/**
Example
{
  code: {
    code: 'burn'
    display: 'Burn',
  },
  subcodes: [
    {
      code: 'flame',
      display: 'Flame',
    },
    {
      code: 'T31.21',
      display: '20-29% TBSA with 10-19% third degree burns',
      system: 'ICD-10',
    },
  ]
}
*/

// Create schema
const schema = {
  identifier: Joi.any().optional(),
  state: Joi.string().valid( ...Object.keys(STATES) ).required(),
  conditionId: Joi.string().required(),
  eocId: Joi.string().required(),
  // Highest
  code: Joi.object().keys( codeSchema ).optional(),
  subcodes: Joi.array().items ( Joi.object().keys( codeSchema ) ).optional(),
  clinicalStatus: Joi.object().keys( codeSchema ).optional(),
  verificationStatus: Joi.object().keys( codeSchema ).optional(),
  subject: Joi.string().optional(),
  onsetDateTime: Joi.string().optional(),
  onsetAge: Joi.string().optional(),
  onsetPeriodStart: Joi.string().optional(),
  onsetPeriodEnd: Joi.string().optional(),
  onsetRange: Joi.string().optional(),
  onsetString: Joi.string().optional(),
  abatementDateTime: Joi.string().optional(),
  abatementAge: Joi.string().optional(),
  abatementPeriodStart: Joi.string().optional(),
  abatementPeriodEnd: Joi.string().optional(),
  abatementRange: Joi.string().optional(),
  abatementString: Joi.string().optional(),
  recordedDate: Joi.string().optional(),
  recorder: Joi.string().optional(),
  asserter: Joi.string().optional(),
  stage: Joi.array().items(stageSchema).optional(),
  evidence: Joi.array().items(evidenceSchema).optional(),

  // Legacy
  category: Joi.array().items(codeSchema).optional(),
  severity: Joi.object().keys( codeSchema ).optional(),
};

// Define class
class Condition extends DataTableBase {
  constructor ( obj ) {
    joiValidateObj(schema, obj);
    super(
      identifiers.condition.buildPk(obj.eocId),
      identifiers.condition.buildSk(obj.state, obj.eocId, obj.conditionId),
    );
    for (const key of Object.keys(obj)) {
      if(obj[key] || obj[key] === false) {
        this[key] = obj[key];
      }
    }
  }
}
exports.Condition = Condition;
exports.conditionSchema = schema;
